// ============================================================
// SightWedges - what the opposition sees, on the display (plan map-display,
// H; the rulings: wedges longer in the light, the sweep's tempo the rung's).
//
// Each watcher on the map sweeps a wedge round itself: brighter out to what
// it sees in the dark (6 m; 11 m with Dark Sight - the long wedge in the dark
// is the tell), fainter on to what it sees in the light (11 m). The sweep
// turns once per `sweepPeriod(rung)` seconds, each watcher from its own
// seeded bearing; a screen that holds still shows it at rest. A watcher within
// a loud noise's reach turns to it and stares (Z2), and the party's leader
// sweeps a narrow wedge of their own, quicker up the ladder. Watchers are
// only those this screen holds a token for: a player's screen never holds a
// hidden figure's, so never draws its wedge. Looks only; the rules read
// reach and line of sight (engine/stealth/sight).
// ============================================================
import { memo, useContext, useLayoutEffect, useRef, useState } from 'react';
import { sweepFrameMs } from '../../engine/display/sweepPace';
import { projectGround, type Camera } from '../../engine/battlefield/projection';
import type { Token } from '../../engine/battlefield/tokens';
import { between, rngFor } from '../../engine/display/rng';
import { SIGHT_LIT_M, sightReach, sweepPeriod } from '../../engine/stealth/sight';
import { hearsNoise, leaderSweepPeriod, type NoiseAt } from '../../engine/stealth/alerts';
import { bearingBetween } from '../../engine/battlefield/facing';
import type { Rung } from '../../engine/stealth/ladder';
import useDisplayClock from '../../hooks/useDisplayClock';
import useHoldsStill from '../../hooks/useHoldsStill';
import { GpuWedgesContext, type GpuWedges } from './displayFaces';
import { arcBearings, LEADER_HALF, LEADER_M, WEDGE_HALF } from '../../engine/display/wedgeShape';
import styles from '../../styles/Display.module.css';

/** The wedge from `foot` out to `reach` metres about `facing` degrees, as screen points. */
function wedge(
  foot: { x: number; z: number },
  reach: number,
  facing: number,
  camera: Camera,
  half = WEDGE_HALF,
): string {
  const rim = arcBearings(facing, half).map((a) => {
    const r = (a * Math.PI) / 180;
    return projectGround({ x: foot.x + reach * Math.cos(r), z: foot.z + reach * Math.sin(r) }, camera);
  });
  return [projectGround(foot, camera), ...rim].map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
}

/** The periods of what sweeps on this screen: the watchers' (one tempo for all) and the leader's. */
const periodsOf = (watchers: boolean, leader: boolean, rung: Rung): number[] => [
  ...(watchers ? [sweepPeriod(rung)] : []),
  ...(leader ? [leaderSweepPeriod(rung)] : []),
];

function SightWedges({
  watchers,
  tokens,
  rung,
  camera,
  heard = null,
  leaderId = null,
}: {
  watchers: readonly { id: string; darkSight: boolean }[] | undefined;
  tokens: Readonly<Record<string, Token>>;
  rung: Rung;
  camera: Camera;
  /** The noise the wedges turn to (Z2: engine/stealth/alerts `noiseHeard`), or null. */
  heard?: NoiseAt | null;
  /** The party's leader (Z2): their own sweep, quicker up the ladder. */
  leaderId?: string | null;
}) {
  const shown = (watchers ?? []).filter((w) => tokens[w.id]);
  const leader = leaderId ? tokens[leaderId] : undefined;
  // While the canvas draws the map, the wedges turn on a CSS plane over it (WedgePlane): the svg keeps only what
  // the specs read, and no clock runs here.
  const publish = useContext(GpuWedgesContext);
  const handed = { shown, tokens, heard, leader, rung, still: useHoldsStill() };
  usePublished(publish, handed);
  if (!anyWatching(handed)) return null;
  if (publish) return <WedgeMirror shown={shown} tokens={tokens} heard={heard} rung={rung} leader={!!leader} />;
  return <SvgWedges handed={handed} camera={camera} />;
}

/** The wedges drawn in the svg (the svg map, the fallback): their own clock, as fast as the fastest sweep needs. */
function SvgWedges({ handed, camera }: { handed: Handed; camera: Camera }) {
  const { shown, tokens, heard, leader, rung, still } = handed;
  const now = useSweepClock(sweepFrameMs(periodsOf(shown.length > 0, !!leader, rung)), still);
  const turn = ((now / 1000) % sweepPeriod(rung)) / sweepPeriod(rung);
  return (
    <g data-testid="sight-wedges" data-rung={rung} pointerEvents="none">
      {leader && <LeaderSweep leader={leader} rung={rung} now={now} camera={camera} />}
      {shown.map((w) => {
        // Turned to a noise it heard, a watcher stares: a constant turn, so its memo holds while the rest sweep.
        const turnedTo = hearsNoise(tokens[w.id], heard) ? heard : null;
        return (
          <WatcherWedge
            key={w.id}
            watcher={w}
            at={tokens[w.id]}
            turn={turnedTo ? 0 : turn}
            heard={turnedTo}
            camera={camera}
          />
        );
      })}
    </g>
  );
}

/** One watcher's wedge: what it sees in the dark, and on in the light; turned to the noise it heard (`heard`). */
const WatcherWedge = memo(function WatcherWedge(props: {
  watcher: { id: string; darkSight: boolean };
  at: Token;
  turn: number;
  heard: { x: number; z: number } | null;
  camera: Camera;
}) {
  const { watcher: w, at, turn, heard, camera } = props;
  const facing = facingOf(w.id, at, turn, heard);
  return (
    <g data-watcher={w.id} data-dark-sight={w.darkSight || undefined} data-heard={!!heard || undefined}>
      {/* With Dark Sight the dark reaches as far as the light: one wedge, not the same one twice. */}
      {!w.darkSight && <polygon className={styles.wedgeLit} points={wedge(at, SIGHT_LIT_M, facing, camera)} />}
      <polygon className={styles.wedgeDark} points={wedge(at, sightReach(false, w.darkSight), facing, camera)} />
    </g>
  );
});

/**
 * The sweeps' clock, ms: a new time every `frameMs` (sweepFrameMs: as often as the fastest sweep needs, twenty a
 * second at most - each redraw repaints the map), none when nothing sweeps; 0 - every sweep at rest - on a screen
 * that holds still.
 */
function useSweepClock(frameMs: number | null, still: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useDisplayClock(frameMs !== null && !still, () => {
    const t = Date.now();
    setNow((was) => (t - was >= (frameMs ?? 0) ? t : was));
  });
  return still ? 0 : now;
}

/** Where a watcher looks: at a noise it heard (Z2: it turns at once and stares), else round its seeded sweep. */
function facingOf(id: string, t: Token, turn: number, heard: { x: number; z: number } | null): number {
  return heard ? bearingBetween(t, heard) : between(rngFor(id, 'sweep'), 0, 360) + turn * 360;
}

/** The leader's sweep (Z2, the stealth page): a narrow wedge LEADER_M out, round once in `leaderSweepPeriod(rung)`. */
function LeaderSweep({ leader, rung, now, camera }: { leader: Token; rung: Rung; now: number; camera: Camera }) {
  const period = leaderSweepPeriod(rung);
  const facing = (((now / 1000) % period) / period) * 360;
  return (
    <polygon
      data-testid="leader-sweep"
      className={styles.leaderSweep}
      points={wedge(leader, LEADER_M, facing, camera, LEADER_HALF)}
    />
  );
}

type Watcher = { id: string; darkSight: boolean };
interface Handed {
  shown: readonly Watcher[];
  tokens: Readonly<Record<string, Token>>;
  heard: NoiseAt | null;
  leader: Token | undefined;
  rung: Rung;
  still: boolean;
}

/** What the plane draws: each watcher's foot, seeded bearing and the bearing it stares along, if it heard a noise. */
function wedgesOf({ shown, tokens, heard, leader, rung, still }: Handed): GpuWedges {
  const watchers = shown.map((w) => {
    const at = tokens[w.id];
    const stare = hearsNoise(at, heard) ? bearingBetween(at, heard!) : null;
    return { id: w.id, x: at.x, z: at.z, darkSight: w.darkSight, base: facingOf(w.id, at, 0, null), heard: stare };
  });
  return { rung, still, watchers, leader: leader ? { x: leader.x, z: leader.z } : null };
}

const anyWatching = ({ shown, leader }: Handed) => shown.length > 0 || !!leader;

/** The wedges handed to the plane, once per change of what it draws; withdrawn when they go. */
function usePublished(publish: ((w: GpuWedges | null) => void) | null, handed: Handed) {
  const last = useRef('');
  useLayoutEffect(() => {
    if (!publish) return;
    const wedges = anyWatching(handed) ? wedgesOf(handed) : null;
    const key = JSON.stringify(wedges);
    if (key === last.current) return;
    last.current = key;
    publish(wedges);
  });
  useLayoutEffect(() => {
    if (!publish) return undefined;
    return () => {
      last.current = '';
      publish(null);
    };
  }, [publish]);
}

/** What the specs read of the wedges while the plane draws them: who watches, the rung, who heard, the leader. */
function WedgeMirror({ shown, tokens, heard, rung, leader }: Omit<Handed, 'still' | 'leader'> & { leader: boolean }) {
  return (
    <g data-testid="sight-wedges" data-rung={rung} data-plane="true" pointerEvents="none">
      {leader && <g data-testid="leader-sweep" />}
      {shown.map((w) => (
        <g
          key={w.id}
          data-watcher={w.id}
          data-dark-sight={w.darkSight || undefined}
          data-heard={hearsNoise(tokens[w.id], heard) || undefined}
        />
      ))}
    </g>
  );
}

export default memo(SightWedges);
